import { newTaskSuffix } from '@griply/types';
import { onGoalReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { goalInboxID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';

import { useGoalTree } from '../../hooks/use-goal-tree';
import { GroupedListProps } from '../../types';
import { GoalHeader } from '../goal-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './goal-grouping-tasks-list.style';

const goalDragTypes = [TaskListItemTypes.Task, TaskListItemTypes.Goal];
const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Task],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const GoalGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
  canReorderGoals,
}) => {
  const { displayDue } = groupSettings;
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const goalGroupingRootId = 'goal-grouping-root';

  const treeItems = useGoalTree({
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNoGoalGroup: groupSettings.hideNoGoalGroup,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
    rootId: goalGroupingRootId,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onGoalReorder({
      updatedItems,
      oldItems: Object.values(treeItems),
      tasksMap,
      rootSectionId: rootId,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const goalsRef = useRef(goals);
  goalsRef.current = goals;

  const lifeAreasRef = useRef(lifeAreas);
  lifeAreasRef.current = lifeAreas;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              hideGoal
              displayDue={displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const goalId = item.sourceId.replace(newTaskSuffix, '');
          const goal = goalId === goalInboxID ? undefined : (goalId as ID);
          return (
            <NewTaskRow
              goal={goal}
              date={defaultNewTaskDate}
              startTime={null}
              duration={null}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Goal
        if (type === TaskListItemTypes.Goal) {
          return (
            <GoalHeader
              item={itemInstance}
              goals={goalsRef.current}
              lifeAreas={lifeAreasRef.current}
              denseFormat={denseFormat}
            />
          );
        }

        return null;
      },
    [
      denseFormat,
      defaultNewTaskDate,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
      displayDue,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={goalGroupingRootId}
        items={treeItems}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={canReorderGoals ? goalDragTypes : taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
        dropTypes={dropTypes}
      />
    </Styled.Container>
  );
};
